import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faClipboardCheck,
  faGear,
  faGlobe,
  faMobileScreenButton,
  faMousePointer,
  faPhone,
  faPlug,
  faRobot,
  faServer,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import aiAgentVideo from '../assets/video/ai_agent.mp4';

const Products = () => {
  const [searchParams] = useSearchParams();
  const [activeProduct, setActiveProduct] = useState(searchParams.get('product') || 'c2c');

  useEffect(() => {
    const product = searchParams.get('product');
    if (['c2c', 'softphone', 'appliance', 'ai'].includes(product)) {
      setActiveProduct(product);
    }
  }, [searchParams]);

  return (
    <section id="products" className="products-section">
      <div className="section-container">
        <div className="section-header reveal">
          <div className="section-label">Customer-centric Products</div>
          <h2 className="section-title">The C2C Ecosystem</h2>
          <p className="section-description">
            Four powerful products, one unified platform — built to connect your customers to the right person, 
            at the right moment, with full context.
          </p>
        </div>

        <Tabs value={activeProduct} onValueChange={setActiveProduct} className="products-tabs reveal">
          <TabsList className="tabs-list">
            <TabsTrigger value="c2c">Context to Call</TabsTrigger>
            <TabsTrigger value="softphone">C2C Softphone</TabsTrigger>
            <TabsTrigger value="appliance">C2C Appliance</TabsTrigger>
            <TabsTrigger value="ai">C2C AI Agent</TabsTrigger>
          </TabsList>

          <TabsContent value="c2c" className="product-panel">
            <div className="product-grid">
              <div className="product-info">
                <Badge className="product-badge core">
                  <FontAwesomeIcon icon={faPlug} />
                  Core Platform
                </Badge>
                <h3 className="product-title">Context to Call</h3>
                <p className="product-description">
                  The flagship platform that puts <strong>who, what, and why</strong> into every customer interaction. 
                  Embed smart Call, SMS, and Email buttons into your website — customers connect instantly with full context.
                </p>
                <ul className="feature-list">
                  <li><FontAwesomeIcon icon={faCheck} /> One-click Call, SMS, Email buttons embedded on any page</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Real-time customer context delivered before every interaction</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Icon-to-Agent Mapping — route callers to the exact right rep</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Shopify, Magento, Wix, WordPress integrations</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Browser-based calling — 112+ countries, zero downloads</li>
                  <li><FontAwesomeIcon icon={faCheck} /> BI-powered analytics & CRM API integrations</li>
                </ul>
                <div className="product-actions">
                  <Button asChild>
                    <a href="https://sso.contexttocall.com/realms/c2cp1/protocol/openid-connect/registrations?client_id=c2c-biz-app&redirect_uri=https%3A%2F%2Fapp.contexttocall.com&response_type=code" target="_blank" rel="noopener noreferrer">
                      Get Started Free
                    </a>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/features">
                      View Features
                    </Link>
                  </Button>
                </div>
              </div>
              <div className="product-visual core-visual">
                <div className="visual-content">
                  <FontAwesomeIcon icon={faPlug} className="visual-icon" />
                  <h4>Embed in Minutes</h4>
                  <p>Low-code embed — generate code from the web app, paste onto any webpage, and start receiving context-rich contacts immediately.</p>
                  <div className="code-block">
                    <code>
{`<script src="c2c.js" data-key="YOUR_KEY" data-mode="call,sms,email"> </script>
<label class="Ns3iSndZjCeqn8t19378" id="Ns3iSndZjCeqn8t19378" for="c2c_call"></label>
<label class="gdoAqsMqJn7mylo18Tpk" id="gdoAqsMqJn7mylo18Tpk" for="c2c_sms"></label>
<label class="jZuL2b9WUG9YdK8E8IXW" id="jZuL2b9WUG9YdK8E8IXW" for="c2c_email"></label>`}
                  </code>
                  </div>
                  <div className="platform-tags">
                    <Badge variant="outline">Shopify</Badge>
                    <Badge variant="outline">WordPress</Badge>
                    <Badge variant="outline">Magento</Badge>
                    <Badge variant="outline">Wix</Badge>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="softphone" className="product-panel">
            <div className="product-grid">
              <div className="product-info">
                <Badge className="product-badge softphone">
                  <FontAwesomeIcon icon={faMobileScreenButton} />
                  Softphone
                </Badge>
                <h3 className="product-title">C2C Softphone</h3>
                <p className="product-description">
                  A professional-grade browser and app-based softphone that gives your agents a unified communication hub. 
                  Handle calls, SMS, and emails from one intelligent interface — with full customer context.
                </p>
                <ul className="feature-list">
                  <li><FontAwesomeIcon icon={faCheck} /> Works on any browser — no hardware phone needed</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Unified inbox for calls, SMS, and email communications</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Low-latency VoIP API for crystal-clear call quality</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Intelligent media integrations for on-the-go agents</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Agent availability management and routing controls</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Real-time call transcription and notes</li>
                </ul>
                <div className="product-actions">
                  <Button className="btn-softphone" asChild>
                    <Link to="/softphone">
                      Explore Softphone
                    </Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <a href="https://outlook.office365.com/book/RequestDemo@vgroupinc.com/">
                      Request Demo
                    </a>
                  </Button>
                </div>
              </div>
              <div className="product-visual softphone-visual">
                <div className="visual-content">
                  <div className="softphone-product-demo">
                    <div className="softphone-product-topbar">
                      <div className="softphone-window-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                      <div className="softphone-product-url">
                        <span></span>
                        app.your-crm.com/leads
                      </div>
                    </div>
                    <div className="softphone-product-body">
                      <div className="softphone-product-meta">
                        <span>Leads - 4</span>
                        <span>softphone.detect()</span>
                      </div>
                      <div className="softphone-product-row">
                        <div>
                          <strong>Ashley Davis</strong>
                          <span>VP Sales - Acme Corp</span>
                        </div>
                        <small>+1 (415) 555 0142</small>
                      </div>
                      <div className="softphone-product-row is-active">
                        <div>
                          <strong>Lina Park</strong>
                          <span>Founder - Northwind</span>
                        </div>
                        <div className="softphone-product-call">
                          <small>+44 20 7946 0123</small>
                          <button type="button" aria-label="Call with Softphone">
                            <FontAwesomeIcon icon={faPhone} />
                          </button>
                        </div>
                      </div>
                      <div className="softphone-product-row">
                        <div>
                          <strong>Diego Alvarez</strong>
                          <span>Head Recruiter - Helio</span>
                        </div>
                        <small>+34 911 23 45 67</small>
                      </div>
                      <div className="softphone-product-status">
                        <div>
                          <i></i>
                          <span>
                            <strong>Calling - Softphone</strong>
                            <small>+44 20 7946 0123</small>
                          </span>
                        </div>
                        <em>00:02</em>
                      </div>
                      <FontAwesomeIcon icon={faMousePointer} className="softphone-product-cursor" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="appliance" className="product-panel">
            <div className="product-grid">
              <div className="product-info">
                <Badge className="product-badge appliance">
                  <FontAwesomeIcon icon={faServer} />
                  Best for Appliance Industry
                </Badge>
                <h3 className="product-title">C2C Appliance</h3>
                <p className="product-description">
                  Connect customers, dealers, and service teams through one platform for faster support, smarter engagement, and better service experiences.

                </p>
                <ul className="feature-list">
                  <li><FontAwesomeIcon icon={faCheck} /> Centralize customer, dealer, and service communications in one platform</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Automate service updates, appointment reminders, and follow-ups</li>
                  <li><FontAwesomeIcon icon={faCheck} /> AI-powered voice agents for 24/7 customer support and inquiry handling</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Faster warranty, installation, and repair request management</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Connect sales, support, and field service teams for seamless collaboration</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Improve customer satisfaction with real-time, omnichannel engagement</li>
                </ul>
                <div className="product-actions">
                  <Button className="btn-appliance" asChild>
                    <a href="#contact">
                      Talk to Sales
                    </a>
                  </Button>
                  <Button variant="outline" asChild>
                    <a href="https://outlook.office365.com/book/RequestDemo@vgroupinc.com/" target="_blank" rel="noopener noreferrer">
                      Book a Demo
                    </a>
                  </Button>
                </div>
              </div>
              <div className="product-visual appliance-visual">
                <div className="visual-content">
                  <FontAwesomeIcon icon={faServer} className="visual-icon" />
                  <h4>Built for Enterprise</h4>
                  <div className="enterprise-grid">
                    <div className="enterprise-item">
                      <div className="item-icon"><FontAwesomeIcon icon={faShieldHalved} /></div>
                      <div className="item-name">Data Sovereignty</div>
                      <div className="item-desc">100% On-Premise</div>
                    </div>
                    <div className="enterprise-item">
                      <div className="item-icon"><FontAwesomeIcon icon={faGear} /></div>
                      <div className="item-name">PBX Integration</div>
                      <div className="item-desc">Existing Infrastructure</div>
                    </div>
                    <div className="enterprise-item">
                      <div className="item-icon"><FontAwesomeIcon icon={faClipboardCheck} /></div>
                      <div className="item-name">CMMI & ISO</div>
                      <div className="item-desc">Audit Ready</div>
                    </div>
                    <div className="enterprise-item">
                      <div className="item-icon"><FontAwesomeIcon icon={faGlobe} /></div>
                      <div className="item-name">White-Label</div>
                      <div className="item-desc">Full Branding</div>
                    </div>
                  </div>
                  <Badge className="enterprise-badge">Trusted by Healthcare, Banking, Pharma & Legal</Badge>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="ai" className="product-panel">
            <div className="product-grid">
              <div className="product-info">
                <Badge className="product-badge ai">
                  <FontAwesomeIcon icon={faRobot} />
                  AI-Powered
                </Badge>
                <h3 className="product-title">C2C AI Agent</h3>
                <p className="product-description">
                  An intelligent, always-on AI agent that handles customer interactions 24/7 in 30+ languages. 
                  Reduce costs, eliminate wait times, and deliver personalized, context-aware conversations.
                </p>
                <ul className="feature-list">
                  <li><FontAwesomeIcon icon={faCheck} /> 24/7 autonomous customer engagement with zero downtime</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Responds in 30+ languages — truly global reach</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Context-aware: understands which page customer is on</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Intelligent handoff to human agents when needed</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Spam filtering — only qualified, real customer interactions</li>
                  <li><FontAwesomeIcon icon={faCheck} /> Pay-per-use AI minutes — scale cost-efficiently</li>
                </ul>
                <div className="product-actions">
                  <Button className="btn-ai" asChild>
                    <a href="https://sso.contexttocall.com/realms/c2cp1/protocol/openid-connect/registrations?client_id=c2c-biz-app&redirect_uri=https%3A%2F%2Fapp.contexttocall.com&response_type=code" target="_blank" rel="noopener noreferrer">
                      Try AI Agent
                    </a>
                  </Button>
                  <Button variant="outline" asChild>
                    <a href="#contact">
                      Request Demo
                    </a>
                  </Button>
                </div>
              </div>
              <div className="product-visual ai-visual">
                <div className="visual-content ai-image-content">
                  <video
                    src={aiAgentVideo}
                    className="ai-agent-video"
                    autoPlay
                    muted
                    loop
                    playsInline
                    aria-label="AI agent handling customer calls and routing context"
                  />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default Products;
